import { Auth, cookieUtils } from '../utils';
import { apiClient, ApiError, PLATFORM, PLAYGROUND } from './apiClient';
import { User } from '../types';

// Mirrors ai-canvas's pattern: the platform-node JWT lives in a client-side
// cookie (Authorization) and is attached to every API call as a Bearer header
// via apiClient. No BFF; the frontend talks to platform-node and the
// playground server directly.

interface BackendUser {
    _id: string;
    email: string;
    name?: string;
    picture?: string;
    role?: string;
}

export const getUserFromToken = async (): Promise<User | null> => {
    try {
        const token = cookieUtils.get(Auth.TOKEN);
        if (!token) {
            return null;
        }
        const backendUser = await apiClient.get<BackendUser>('/user/user-info-from-token', PLATFORM);
        return {
            id: backendUser._id,
            email: backendUser.email,
            name: backendUser.name || '',
            picture: backendUser.picture,
            isAuthenticated: true,
        };
    } catch (error) {
        if (error instanceof ApiError) {
            console.error('Token validation failed:', error.status, error.message);
        } else {
            console.error('Error fetching user from token:', error);
        }
        return null;
    }
};

export const logout = () => {
    cookieUtils.delete(Auth.TOKEN);
    cookieUtils.deleteV2(Auth.TOKEN);
    window.location.href = '/';
};

export const isAuthenticated = () => {
    return !!cookieUtils.get(Auth.TOKEN);
};

export const setToken = (token: string) => {
    cookieUtils.create(
        Auth.TOKEN,
        token,
        Auth.EXPIRING_IN_DAYS,
        window.location.hostname
    );
    // Strip the token from the URL so it doesn't linger in history / referrer.
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url.toString());
    } catch (err) {
        console.warn('[auth] failed to clean token from URL', err);
    }
};

export const getToken = (): string | null => {
    return cookieUtils.get(Auth.TOKEN) || null;
};

export interface UserDetails {
    _id: string;
    name: string;
    email: string;
    handle?: string;
    picture?: string;
    role?: string;
    grade?: string;
    boards?: string[];
    timezone?: string;
}

export interface UserDetailsListItem {
    _id: string;
    name: string;
    email: string;
    role?: string;
    grade?: string;
    boards?: string[];
    timezone?: string;
}

const BATCH_SIZE = 20;

/**
 * PII decryption used to be done by calling platform-node directly from the
 * browser with a service API key. That key shipped inside the React bundle,
 * which was a real security hole. We now proxy through the playground server
 * — it adds the API key server-side and gates access with the user's normal
 * auth, so the browser never sees the platform key.
 */
export const getUserDetailsByIds = async (userIds: string[]): Promise<UserDetailsListItem[]> => {
    if (!userIds.length) return [];

    const chunks: string[][] = [];
    for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
        chunks.push(userIds.slice(i, i + BATCH_SIZE));
    }

    const results = await Promise.all(
        chunks.map(async (chunk) => {
            try {
                const response = await apiClient.post<{ success: boolean; data: UserDetailsListItem[] }>(
                    '/api/users/details',
                    PLAYGROUND,
                    { userIds: chunk }
                );
                return response?.success && response.data ? response.data : [];
            } catch (error) {
                console.warn('[getUserDetailsByIds] Failed to fetch batch', error);
                return [];
            }
        })
    );

    return results.flat();
};

export const getUserDetailsById = async (userId: string): Promise<UserDetails | null> => {
    try {
        const response = await apiClient.get<{ success: boolean; data: UserDetails }>(
            `/api/users/${encodeURIComponent(userId)}/details`,
            PLAYGROUND
        );
        if (response?.success && response.data) return response.data;
        return null;
    } catch (error) {
        console.warn('[getUserDetailsById] Failed to fetch user details for', userId, error);
        return null;
    }
};
